/**
 * AI Service for generating drunk startup ideas
 * Now uses Firebase Functions for security (tokens stay on backend)
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

/**
 * Generate a drunk startup idea based on intoxication level
 * Calls Firebase Function which securely handles AI API
 * @param {number} intoxicationLevel - Level from 0 to 3 (in ‰)
 * @returns {Promise<{title: string, desc: string, degree: string}>}
 */
export async function generateDrunkIdea(intoxicationLevel) {
  try {
    // Call Firebase Function (secure backend)
    const generateIdea = httpsCallable(functions, 'generateIdea');
    const result = await generateIdea({ intoxicationLevel });

    return result.data;
  } catch (error) {
    console.error('Error generating idea:', error);
    // Fallback to mock idea if function fails
    return getFallbackIdea(intoxicationLevel);
  }
}

// Fallback ideas (same as backend)
function getFallbackIdea(level) {
  const fallbacks = [
    {
      title: "Uber for Pigeons",
      desc: "Trained pigeons deliver micro-cargo (USB drives, SD cards) across the city. No traffic jams, works for seeds.",
      degree: `${level}‰`
    },
    {
      title: "NFT Toilet Paper",
      desc: "Each roll is unique on blockchain. Burn tokens in real-time as you use. Proof-of-Wipe consensus mechanism.",
      degree: `${level}‰`
    },
    {
      title: "LinkedIn for Introverts",
      desc: "Professional network where messaging is banned. Just silently endorse skills with a gentle nod emoji.",
      degree: `${level}‰`
    },
    {
      title: "Shazam for Baby Crying",
      desc: "AI identifies why baby screams: hungry, sleepy, needs change, or existential dread of consciousness.",
      degree: `${level}‰`
    },
    {
      title: "Tinder for Socks",
      desc: "Computer vision matches your lonely sock with its soulmate across citywide laundromat databases.",
      degree: `${level}‰`
    }
  ];

  return fallbacks[Math.floor(Math.random() * fallbacks.length)];
}
